import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";

// Bandera propia que lee el interceptor de abajo (omitir x-company-id).
declare module "axios" {
  interface AxiosRequestConfig {
    _skipCompanyScope?: boolean;
  }
}
import Swal from "sweetalert2";
import {
  clearSession,
  isTokenExpired,
  refreshToken,
  shouldRefreshToken,
  isLoggingOut,
  setLoggingOut,
} from "./tokenUtils";
import {
  describeAxiosError,
  trackDiagnostic,
  trackTokenWeight,
} from "./diagnostics";

// Local-emulator project ID for Dumax-MonoRepo is always tdc-development-846de
// (see apps/v5/.env comment). Anything else silently splits Firestores.
const API_URL =
  import.meta.env.VITE_FIREBASE_API_URL ||
  "http://127.0.0.1:5001/tdc-development-846de/us-central1/torreDeControlApi";

declare module "axios" {
  export interface AxiosRequestConfig {
    /**
     * Silencia la alerta de "sesión expirada" para peticiones que corren ANTES
     * de que exista sesión de backend — el login valida geocerca y horario
     * antes de crearla, y ahí `validateActiveSession` responde 403
     * NO_ACTIVE_SESSION de forma esperada, no porque la sesión se haya caído.
     */
    skipSessionExpiredAlert?: boolean;
  }
}

// Flag para evitar múltiples alertas de sesión expirada
let isShowingSessionExpiredAlert = false;

// Admin abort signal: cancels in-flight GET requests when company changes
let _adminAbortSignal: AbortSignal | null = null;

export function setAdminAbortSignal(signal: AbortSignal | null) {
  _adminAbortSignal = signal;
}

export function isAbortError(error: unknown): boolean {
  return (
    axios.isCancel(error) ||
    (error instanceof Error && error.name === "AbortError")
  );
}

export function isAxiosClientTimeout(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response != null) return false;
  const code = error.code;
  const msg = (error.message || "").toLowerCase();
  return code === "ECONNABORTED" || msg.includes("timeout");
}

const SESSION_INVALID_KEYS = [
  "SESSION_CLOSED",
  "SESSION_NOT_FOUND",
  "SESSION_UNAUTHORIZED",
  "NO_ACTIVE_SESSION",
  "SessionNotFound",
  "SessionInactive",
  "InvalidSession",
];

function trackSessionExpired(error: AxiosError, reason: string) {
  trackDiagnostic("session_expired", { reason, ...describeAxiosError(error) });
}

function trackApiFailure(error: AxiosError) {
  const status = error.response?.status;
  const isServerError = status !== undefined && status >= 500;
  if (error.response && !isServerError) return;
  const kind = isAxiosClientTimeout(error)
    ? "timeout"
    : error.response
      ? "server_error"
      : "network";
  const details = describeAxiosError(error);
  trackDiagnostic(
    "api_error",
    { kind, method: error.config?.method?.toUpperCase(), ...details },
    `api_error:${kind}:${details.endpoint}`,
  )
}

export function isSessionInvalidError(error: AxiosError): boolean {
  const body = error.response?.data as
    | {
        meta?: { messageKey?: string; message?: string };
        messageKey?: string;
        message?: string;
      }
    | undefined;
  const messageKey = body?.meta?.messageKey ?? body?.messageKey;
  if (messageKey && SESSION_INVALID_KEYS.includes(messageKey)) return true;
  // validateHelper manda meta.message como objeto ({422: [{msg}]}): sin el typeof
  // el interceptor tronaba con TypeError y tapaba el error de validación real.
  const raw = body?.meta?.message ?? body?.message;
  const text = typeof raw === "string" ? raw.toLowerCase() : "";
  return text.includes("session") || text.includes("sesión");
}

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

const getCurrentLang = (): string => {
  const i18nLang = localStorage.getItem("i18nextLng");
  if (i18nLang) {
    return i18nLang.split("-")[0];
  }
  return navigator.language.split("-")[0] || "es";
};

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Si estamos en proceso de logout, cancelar la petición silenciosamente
    if (isLoggingOut()) {
      return Promise.reject(new axios.Cancel("Logging out"));
    }

    // Rutas públicas que no requieren autenticación
    const publicRoutes = [
      "/auth/request-password-reset",
      "/auth/validate-password-reset-token",
      "/auth/execute-password-reset",
      "/auth/request-session-reset",
      "/auth/execute-session-reset",
    ];

    const isPublicRoute = publicRoutes.some((route) =>
      config.url?.includes(route),
    );

    // Si es una ruta pública, no agregar headers de autenticación
    if (isPublicRoute) {
      return config;
    }

    const token = localStorage.getItem("token");
    const sessionId = localStorage.getItem("sessionId");
    const companyId = localStorage.getItem("companyId");
    const userId = localStorage.getItem("userId");

    // Bandera explícita para flujos reseller multi-tenant. Cuando es `true`
    // omitimos por completo la inyección de `x-company-id` en headers y
    // `company_id` en query/body. Necesario porque el truco de pasar
    // `headers: { "x-company-id": "" }` no funcionaba: el chequeo falsy de
    // abajo lo sobreescribía con el companyId del localStorage.
    const skipCompanyScope =
      (config as InternalAxiosRequestConfig & { _skipCompanyScope?: boolean })
        ._skipCompanyScope === true;

    if (token && isTokenExpired(token)) {
      clearSession();
      return Promise.reject(new axios.Cancel("Token expired"));
    }

    if (token && shouldRefreshToken(token) && !isRefreshing) {
      isRefreshing = true;
      const refreshed = await refreshToken();
      isRefreshing = false;

      if (refreshed) {
        const newToken = localStorage.getItem("token");
        if (newToken) {
          onTokenRefreshed(newToken);
        }
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      trackTokenWeight(token);
    }

    if (sessionId) {
      config.headers["x-session-id"] = sessionId;
    }

    if (userId) {
      config.headers["x-user-id"] = userId;
    }

    if (companyId && !skipCompanyScope) {
      if (!config.headers["x-company-id"]) {
        config.headers["x-company-id"] = companyId;
      }
      if (config.method === "post" || config.method === "put") {
        // No agregar company_id al endpoint /company (para gestión de compañías)
        const isCompanyEndpoint = config.url?.includes("/company");
        
        if (config.data && !isCompanyEndpoint) {
          if (typeof config.data === "string") {
            try {
              config.data = JSON.parse(config.data);
            } catch (e) {
              console.error("Error parsing data:", e);
            }
          }

          // Solo agregar company_id si no existe en los datos (no sobrescribir)
          // Verificar explícitamente undefined para no sobrescribir null o string vacío
          if (
            typeof config.data === "object" &&
            config.data !== null &&
            config.data.company_id === undefined
          ) {
            config.data = {
              ...config.data,
              company_id: companyId,
            };
          }
        } else if (!config.data && !isCompanyEndpoint) {
          config.data = { company_id: companyId };
        }
      }
    }

    config.params = {
      ...config.params,
      lang: config.params?.lang || getCurrentLang(),
    };

    // Agregar company_id a los parámetros para peticiones GET (solo si no fue establecido explícitamente)
    // Excluir endpoint /company para gestión de compañías
    const isCompanyEndpoint = config.url?.includes("/company");
    if (
      companyId &&
      config.method === "get" &&
      !isCompanyEndpoint &&
      !skipCompanyScope
    ) {
      if (!config.params?.company_id) {
        config.params = {
          ...config.params,
          company_id: companyId,
        };
      }
    }

    if (_adminAbortSignal && !config.signal && config.method === "get") {
      config.signal = _adminAbortSignal;
    }

    (config as InternalAxiosRequestConfig & { requestStartedAt?: number }).requestStartedAt =
      Date.now();

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Muestra alerta de sesión expirada y redirige al login
 */
const showSessionExpiredAlert = async () => {
  if (isShowingSessionExpiredAlert || isLoggingOut()) return;

  isShowingSessionExpiredAlert = true;
  setLoggingOut(true);

  // Cerrar cualquier SweetAlert abierto
  Swal.close();

  const lang = localStorage.getItem("i18nextLng")?.split("-")[0] || "es";
  const messages = {
    es: {
      title: "Sesión expirada",
      text: "Tu sesión ha expirado o fue cerrada. Por favor, inicia sesión nuevamente.",
      button: "Ir al Login",
    },
    en: {
      title: "Session expired",
      text: "Your session has expired or was closed. Please log in again.",
      button: "Go to Login",
    },
  };

  const msg = messages[lang as keyof typeof messages] || messages.es;

  await clearSession(false); // No intentar cerrar sesión en backend (ya no es válida)

  await Swal.fire({
    icon: "warning",
    title: msg.title,
    text: msg.text,
    confirmButtonText: msg.button,
    confirmButtonColor: "#16a34a",
    allowOutsideClick: false,
    allowEscapeKey: false,
  });

  isShowingSessionExpiredAlert = false;
  setLoggingOut(false);
  window.location.href = "/";
};

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    // Handle 304 Not Modified as successful response
    if (error.response?.status === 304) {
      return error.response;
    }

    // Si es una cancelación por logout, no propagar el error
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // Si estamos en proceso de logout o mostrando alerta, ignorar errores
    if (isLoggingOut() || isShowingSessionExpiredAlert) {
      return Promise.reject(new axios.Cancel("Logging out"));
    }

    trackApiFailure(error);

    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Peticiones previas a la sesión: el 403 es esperado, no una sesión caída.
    const skipSessionAlert = originalRequest?.skipSessionExpiredAlert === true;

    // Rutas públicas que no requieren manejo de sesión
    const publicRoutes = [
      "/auth/request-password-reset",
      "/auth/validate-password-reset-token",
      "/auth/execute-password-reset",
      "/auth/request-session-reset",
      "/auth/execute-session-reset",
    ];

    const isPublicRoute = publicRoutes.some((route) =>
      originalRequest.url?.includes(route),
    );

    // Si es una ruta pública, no manejar errores de sesión
    if (isPublicRoute) {
      return Promise.reject(error);
    }

    // Verificar si es un error de sesión inválida/expirada
    const isSessionInvalid = isSessionInvalidError(error);

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const refreshed = await refreshToken();
          isRefreshing = false;

          if (refreshed) {
            const newToken = localStorage.getItem("token");
            if (newToken) {
              onTokenRefreshed(newToken);
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return api(originalRequest);
            }
          }
        } catch (refreshError) {
          isRefreshing = false;
        }
      } else {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      // No se pudo refrescar el token, mostrar alerta
      if (!skipSessionAlert) {
        trackSessionExpired(error, "refresh_failed");
        await showSessionExpiredAlert();
      }
      return Promise.reject(error);
    }

    if (
      (error.response?.status === 403 || error.response?.status === 404) &&
      isSessionInvalid &&
      !skipSessionAlert
    ) {
      trackSessionExpired(error, "session_invalid");
      await showSessionExpiredAlert();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export default api;

export const apiFormData = axios.create({
  baseURL: API_URL,
  timeout: 60000,
});

apiFormData.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (isLoggingOut()) {
      return Promise.reject(new axios.Cancel("Logging out"));
    }

    // Rutas públicas que no requieren autenticación
    const publicRoutes = [
      "/auth/request-password-reset",
      "/auth/validate-password-reset-token",
      "/auth/execute-password-reset",
      "/auth/request-session-reset",
      "/auth/execute-session-reset",
    ];

    const isPublicRoute = publicRoutes.some((route) =>
      config.url?.includes(route),
    );

    // Si es una ruta pública, no agregar headers de autenticación
    if (isPublicRoute) {
      return config;
    }

    const token = localStorage.getItem("token");
    const sessionId = localStorage.getItem("sessionId");
    const companyId = localStorage.getItem("companyId");

    if (token && isTokenExpired(token)) {
      clearSession();
      return Promise.reject(new axios.Cancel("Token expired"));
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (sessionId) {
      config.headers["x-session-id"] = sessionId;
    }

    if (companyId) {
      config.headers["x-company-id"] = companyId;

      if (config.method === "post" || config.method === "put") {
        if (config.data instanceof FormData) {
          config.data.append("company_id", companyId);
        } else if (config.data && typeof config.data === "object") {
          config.data = {
            ...config.data,
            company_id: companyId,
          };
        } else if (!config.data) {
          config.data = { company_id: companyId };
        }
      }
    }

    config.params = {
      ...config.params,
      lang: config.params?.lang || getCurrentLang(),
    };

    // Agregar company_id a los parámetros para peticiones GET
    if (companyId && config.method === "get") {
      config.params = {
        company_id: companyId,
        ...config.params,
      };
    }

    return config;
  },
  (error) => Promise.reject(error),
);

apiFormData.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Si es una cancelación por logout, no propagar el error
    if (
      axios.isCancel(error) ||
      isLoggingOut() ||
      isShowingSessionExpiredAlert
    ) {
      return Promise.reject(new axios.Cancel("Logging out"));
    }

    const originalRequest = error.config as InternalAxiosRequestConfig;

    // Rutas públicas que no requieren manejo de sesión
    const publicRoutes = [
      "/auth/request-password-reset",
      "/auth/validate-password-reset-token",
      "/auth/execute-password-reset",
      "/auth/request-session-reset",
      "/auth/execute-session-reset",
    ];

    const isPublicRoute = publicRoutes.some((route) =>
      originalRequest.url?.includes(route),
    );

    // Si es una ruta pública, no manejar errores de sesión
    if (isPublicRoute) {
      return Promise.reject(error);
    }

    const isSessionInvalid = isSessionInvalidError(error);

    if (error.response?.status === 401 || isSessionInvalid) {
      await showSessionExpiredAlert();
    }
    return Promise.reject(error);
  },
);
